/**
 * CR-863: supplier price change requests on the Cost › Historical dashboard.
 *
 * The chart's series rides on `dashboard_stats` (`costData.price_change_requests`);
 * its click-through and Excel export come from
 * `/purchasing/orders/price_change_requests/`, scoped by the same query the
 * dashboard was fetched with.
 *
 * CR-8627: the customer side (`costData.customer_price_changes`, the ERP's price
 * changes) uses the same series shape; its list is `/purchasing/orders/customer_price_changes/`.
 */
import api, { type AxiosResponse } from './client';

/** One stack in the chart: a supplier, or "Other suppliers (n)". */
export interface PriceChangeRequestsSeriesItem {
  /** `s<supplier id>` or `other`. */
  key: string;
  name: string;
  /** The suppliers this stack counts (one, or every folded supplier for Other). */
  supplier_ids: number[];
}

/** One week or month of the chart. */
export interface PriceChangeRequestsPoint {
  /** Axis label: 'Mar 26' monthly, '09 Mar' weekly. */
  label: string;
  /** ISO dates the bar covers (clipped to the dashboard period). */
  period_start: string;
  period_end: string;
  /** Requests per series key; a key with none is absent. */
  counts: Record<string, number>;
  total: number;
}

/** `costData.price_change_requests`: null on a schedule-line or mixed scope. */
export interface PriceChangeRequestsSeries {
  period: 'weekly' | 'monthly';
  series: PriceChangeRequestsSeriesItem[];
  points: PriceChangeRequestsPoint[];
  total: number;
}

/** Outcome keys; labels come from the server (the Change Requests tab's words). */
export type PriceChangeRequestOutcome = 'open' | 'accepted' | 'declined' | 'cancelled';

/** One request in the click-through. */
export interface PriceChangeRequestRow {
  id: number;
  requested_on: string;
  supplier_id: number;
  supplier_name: string;
  /** The PO's customer company. */
  site_name: string;
  po_number: string;
  po_line_id: number | null;
  line_number: number | null;
  part_number: string;
  description: string;
  /** The PO's currency, which both prices are in. */
  currency: string;
  price_was: string;
  price_asked: string;
  change_pct: number | null;
  /** The supplier's own reason. */
  reason: string;
  outcome: PriceChangeRequestOutcome;
  outcome_label: string;
}

/** A page of one segment's requests. */
export interface PriceChangeRequestsPage {
  /** Rows under the selected outcome tab. */
  count: number;
  /** Rows in the segment, every outcome. */
  total: number;
  outcome_counts: Record<PriceChangeRequestOutcome, number>;
  outcome_labels: Record<PriceChangeRequestOutcome, string>;
  page: number;
  page_size: number;
  results: PriceChangeRequestRow[];
}

/** The segment and page asked for; `scope` is the dashboard's own query. */
export interface PriceChangeRequestsQuery {
  scope: Record<string, string | number | boolean>;
  periodStart: string;
  periodEnd: string;
  /** The stack's suppliers; omit for every supplier. */
  supplierIds?: number[];
  outcome?: PriceChangeRequestOutcome;
  page?: number;
  pageSize?: number;
}

/** CR-8627: one ERP price change in the customer chart's click-through. */
export interface CustomerPriceChangeRow {
  id: number;
  changed_on: string;
  supplier_id: number;
  supplier_name: string;
  /** The PO's customer company. */
  site_name: string;
  po_number: string;
  po_line_id: number | null;
  line_number: number | null;
  part_number: string;
  description: string;
  /** The PO's currency, which both prices are in. */
  currency: string;
  price_was: string;
  price_new: string;
  change_pct: number | null;
}

/** CR-8627: a page of one segment's ERP price changes. */
export interface CustomerPriceChangesPage {
  count: number;
  total: number;
  page: number;
  page_size: number;
  results: CustomerPriceChangeRow[];
}

/** Flags that only steer what `dashboard_stats` computes; never sent here. */
const STATS_ONLY_PARAMS = new Set(['lazy', 'sections', 'data_mode']);

function toParams(query: PriceChangeRequestsQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  Object.entries(query.scope).forEach(([key, value]) => {
    if (!STATS_ONLY_PARAMS.has(key) && !key.startsWith('include_')) params[key] = value;
  });
  params['period_start'] = query.periodStart;
  params['period_end'] = query.periodEnd;
  if (query.supplierIds && query.supplierIds.length) params['suppliers'] = query.supplierIds.join(',');
  if (query.outcome) params['outcome'] = query.outcome;
  if (query.page) params['page'] = query.page;
  if (query.pageSize) params['page_size'] = query.pageSize;
  return params;
}

/**
 * Price change requests API.
 *
 * @example
 * const { data } = await priceChangeRequestsAPI.list({ scope, periodStart, periodEnd });
 */
export const priceChangeRequestsAPI = {
  /**
   * One segment's requests, newest first, with a count per outcome.
   *
   * @param query - segment, outcome tab and page
   * @returns the page and the segment's outcome counts
   */
  list: (query: PriceChangeRequestsQuery): Promise<AxiosResponse<PriceChangeRequestsPage>> =>
    api.get('/purchasing/orders/price_change_requests/', { params: toParams(query) }),

  /**
   * The segment (under its outcome tab) as an Excel file.
   *
   * @param query - segment and outcome tab
   * @returns the workbook
   */
  exportXlsx: (query: PriceChangeRequestsQuery): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/orders/price_change_requests/', {
      params: { ...toParams(query), export_format: 'xlsx' },
      responseType: 'blob',
    }),
};

/**
 * CR-8627: the customer's ERP price changes behind the "Customer price changes" chart.
 *
 * @example
 * const { data } = await customerPriceChangesAPI.list({ scope, periodStart, periodEnd });
 */
export const customerPriceChangesAPI = {
  /**
   * One segment's ERP price changes, newest first.
   *
   * @param query - segment and page (an outcome is ignored: ERP changes have none)
   * @returns the page
   */
  list: (query: PriceChangeRequestsQuery): Promise<AxiosResponse<CustomerPriceChangesPage>> =>
    api.get('/purchasing/orders/customer_price_changes/', { params: toParams({ ...query, outcome: undefined }) }),

  /**
   * The segment as an Excel file.
   *
   * @param query - the segment
   * @returns the workbook
   */
  exportXlsx: (query: PriceChangeRequestsQuery): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/orders/customer_price_changes/', {
      params: { ...toParams({ ...query, outcome: undefined }), export_format: 'xlsx' },
      responseType: 'blob',
    }),
};
